// pages/NewsDetailPage.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Shield, ArrowLeft, Clock, User, Loader2, AlertCircle } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { Button } from '../components/ui';
import { BASE_URL } from '../constants';
import { getIntlLocale } from '../utils';

export const NewsDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [article, setArticle] = useState(null);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    fetch(`${BASE_URL}/api/news/${id}`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(data => { setArticle(data); setStatus('ok'); })
      .catch(() => setStatus('error'));
  }, [id]);

  const formatDate = (iso) => new Date(iso).toLocaleDateString(getIntlLocale(i18n.language), {
    day: 'numeric', month: 'long', year: 'numeric'
  });

  return (
    <div className="min-h-screen bg-brand-bg text-gray-700 font-sans">
      {/* Навбар */}
      <nav className="fixed top-0 w-full z-50 bg-brand-bg/80 backdrop-blur-xl border-b border-gray-100 px-12 py-6 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <div className="bg-brand-accent p-2 rounded-xs text-gray-900 shadow-lg"><Shield size={24} /></div>
          <span className="text-2xl font-black text-gray-900 tracking-tighter uppercase italic leading-none">
            ISV <span className="text-red-600">CENTRAL ASIA</span>
          </span>
        </div>
        <Button variant="ghost" size="md" icon={<ArrowLeft size={14} />} onClick={() => navigate('/news')}>
          {t('NewsDetailPage.all_news')}
        </Button>
      </nav>

      <div className="pt-40 pb-32 px-12 max-w-3xl mx-auto">

        {status === 'loading' && (
          <div className="flex items-center justify-center py-32 text-gray-500">
            <Loader2 size={32} className="animate-spin" />
          </div>
        )}

        {status === 'error' && (
          <div className="space-y-6">
            <div className="flex items-center gap-3 px-6 py-4 bg-brand-danger border border-brand-danger-border rounded-sm text-brand-danger-text">
              <AlertCircle size={18} /> {t('NewsDetailPage.article_not_found')}
            </div>
            <Button variant="ghost" size="md" onClick={() => navigate('/news')}>
              {t('NewsDetailPage.back_to_news')}
            </Button>
          </div>
        )}

        {status === 'ok' && article && (
          <article className="space-y-8">
            {/* Мета */}
            <div className="flex items-center gap-4 text-caption font-black uppercase tracking-widest text-gray-500">
              <span>{formatDate(article.created_at)}</span>
              <span className="text-gray-700">•</span>
              <span className="flex items-center gap-1.5"><Clock size={10} /> {article.reading_time} {t('NewsDetailPage.minutes_short')}</span>
              <span className="text-gray-700">•</span>
              <span className="flex items-center gap-1.5"><User size={10} /> {article.author}</span>
            </div>

            {/* Заголовок */}
            <h1 className="text-4xl font-black text-gray-900 uppercase italic tracking-tight leading-tight">
              {article.title}
            </h1>

            {/* Анонс */}
            <div className="text-lg text-red-500 italic leading-relaxed border-l-2 border-red-600/40 pl-6">
              <ReactMarkdown components={{ p: ({ children }) => <>{children}</> }}>
                {article.preview}
              </ReactMarkdown>
            </div>

            {/* Разделитель */}
            <div className="border-t border-gray-100" />

            {/* Полный текст — Markdown */}
            <div className="prose prose-gray max-w-none
              prose-headings:font-black prose-headings:uppercase prose-headings:italic prose-headings:tracking-tight
              prose-h2:text-2xl prose-h2:text-gray-900 prose-h2:mt-10 prose-h2:mb-4
              prose-h3:text-xl prose-h3:text-gray-800
              prose-p:text-gray-500 prose-p:leading-relaxed prose-p:italic
              prose-strong:text-gray-900 prose-strong:font-black
              prose-a:text-red-600 prose-a:no-underline hover:prose-a:text-red-500
              prose-li:text-gray-500 prose-li:italic
              prose-code:text-red-500 prose-code:bg-brand-accent-muted prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-sm
              prose-blockquote:border-red-600/40 prose-blockquote:text-gray-500 prose-blockquote:italic
            ">
              <ReactMarkdown>{article.body}</ReactMarkdown>
            </div>
          </article>
        )}
      </div>
    </div>
  );
};
